export const PROMOTION_TYPE = Object.freeze({
  percentage: 508,
  none: 509,
  fixedPrice: 510,
  volume: 511,
  combo: 512,
});

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const positiveNumber = (value) => {
  const number = toNumber(value);
  return number !== null && number > 0 ? number : null;
};

const toBoolean = (value) =>
  value === true || value === 1 || String(value).toLowerCase() === "true";

export const promotionType = (product) =>
  toNumber(product?.idCatTypePromotion);

export const regularPrice = (product) => {
  const price = toNumber(product?.price);
  if (price !== null && price > 0) return price;

  const priceBefore = toNumber(product?.priceBefore);
  if (priceBefore !== null && priceBefore >= 0) return priceBefore;
  return price !== null && price >= 0 ? price : 0;
};

const validDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const promotionIsCurrent = (product, referenceDate = new Date()) => {
  const reference = validDate(referenceDate) || new Date();
  const startDate = validDate(product?.promotionStartDate);
  const endDate = validDate(product?.promotionEndDate);

  if (startDate && reference < startDate) return false;
  if (endDate && reference > endDate) return false;
  return true;
};

export const hasPromotion = (product, referenceDate = new Date()) => {
  const type = promotionType(product);
  return (
    toBoolean(product?.promotion) &&
    type !== PROMOTION_TYPE.none &&
    Object.values(PROMOTION_TYPE).includes(type) &&
    promotionIsCurrent(product, referenceDate)
  );
};

export const isPromotionType = (product, type, referenceDate = new Date()) =>
  hasPromotion(product, referenceDate) && promotionType(product) === type;

export const promotionalPrice = (product) => {
  if (promotionType(product) === PROMOTION_TYPE.percentage) {
    const percentage = toNumber(product?.discountPercentage);
    if (percentage !== null && percentage > 0 && percentage <= 100) {
      return regularPrice(product) * (1 - percentage / 100);
    }
  }

  const priceNow = toNumber(product?.priceNow);
  return priceNow !== null && priceNow >= 0
    ? priceNow
    : regularPrice(product);
};

export const promotionApplies = (product, referenceDate = new Date()) => {
  if (!hasPromotion(product, referenceDate)) return false;

  const quantity = positiveNumber(product?.quantity);
  if (quantity === null) return false;

  switch (promotionType(product)) {
    case PROMOTION_TYPE.percentage: {
      const percentage = toNumber(product?.discountPercentage);
      const priceNow = toNumber(product?.priceNow);
      return (
        (percentage !== null && percentage > 0 && percentage <= 100) ||
        (priceNow !== null && priceNow >= 0)
      );
    }
    case PROMOTION_TYPE.fixedPrice: {
      const priceNow = toNumber(product?.priceNow);
      return priceNow !== null && priceNow >= 0;
    }
    case PROMOTION_TYPE.volume: {
      const minimum = positiveNumber(product?.promotionMinQuantity);
      const priceNow = toNumber(product?.priceNow);
      return (
        minimum !== null &&
        quantity >= minimum &&
        priceNow !== null &&
        priceNow >= 0
      );
    }
    case PROMOTION_TYPE.combo: {
      const comboQuantity = positiveNumber(product?.quantityOfProducts);
      const comboPrice = toNumber(product?.priceNow);
      return (
        comboQuantity !== null &&
        quantity >= comboQuantity &&
        comboPrice !== null &&
        comboPrice >= 0
      );
    }
    default:
      return false;
  }
};

const groupedPromotionTotal = (
  quantity,
  groupQuantity,
  groupPrice,
  unitPrice,
  cumulative,
) => {
  const completedGroups = Math.floor(quantity / groupQuantity);
  const applicableGroups = cumulative ? completedGroups : Math.min(1, completedGroups);
  const promotionalUnits = applicableGroups * groupQuantity;
  const remainingUnits = quantity - promotionalUnits;

  return applicableGroups * groupPrice + remainingUnits * unitPrice;
};

export const productTotal = (product, referenceDate = new Date()) => {
  const quantity = positiveNumber(product?.quantity);
  if (quantity === null) return 0;

  const unitPrice = regularPrice(product);
  if (!promotionApplies(product, referenceDate)) return quantity * unitPrice;

  const type = promotionType(product);
  if (type === PROMOTION_TYPE.combo) {
    const comboQuantity = positiveNumber(product?.quantityOfProducts);
    return groupedPromotionTotal(
      quantity,
      comboQuantity,
      promotionalPrice(product),
      unitPrice,
      true,
    );
  }

  if (type === PROMOTION_TYPE.volume) {
    const minimum = positiveNumber(product?.promotionMinQuantity);
    return groupedPromotionTotal(
      quantity,
      minimum,
      promotionalPrice(product) * minimum,
      unitPrice,
      toBoolean(product?.isCumulative),
    );
  }

  return promotionalPrice(product) * quantity;
};

export const orderSubtotal = (products, referenceDate = new Date()) =>
  (Array.isArray(products) ? products : []).reduce(
    (total, product) => total + productTotal(product, referenceDate),
    0,
  );
